import React from "react";
import { useTable, Column, useSortBy, CellProps } from "react-table";
import moment from "moment";
import { DateCalendar } from "@mui/x-date-pickers";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Receipt, ReceiptFilters, ReceiptType } from "../../../models";
import {
  getReceipts,
  getReceiptsSummary,
  selectReceipts,
  selectGetReceiptsResult,
  selectReceiptsSummary,
  selectGetReceiptsSummaryResult,
} from "../receiptsSlice";
import { RangeTotalBar } from "../RangeTotalBar/RangeTotalBar";
import { getDriversV2 } from "../../DriversV2/driversV2Slice";
import { getVehicles } from "../../Vehicles/vehiclesSlice";
import {
  formatMoney,
  receiptTypeLabel,
  formatReceiptDate,
  confidenceBand,
} from "./receiptDisplay";

import { ReceiptModal } from "../ReceiptModal/ReceiptModal";
import { EditReceiptModal } from "../EditReceiptModal/EditReceiptModal";
import { DeleteReceiptConfirmationModal } from "../DeleteReceiptConfirmationModal/DeleteReceiptConfirmationModal";

import "./ReceiptsTable.scss";

const PAGE_SIZE = 25;

export function ReceiptsTable() {
  const dispatch = useAppDispatch();
  const receipts = useAppSelector(selectReceipts);
  const getReceiptsResult = useAppSelector(selectGetReceiptsResult);
  const summary = useAppSelector(selectReceiptsSummary);
  const summaryResult = useAppSelector(selectGetReceiptsSummaryResult);
  // driversV2 is the current slice that exposes a full driver list.
  // driversV2 is being retired in favour of driversSlice, but driversSlice
  // does not yet expose a user/driver list. Using driversV2 as v1 acceptable
  // fallback — note in report when driversSlice gains a list to migrate here.
  const drivers = useAppSelector((s) => s.driversV2.drivers);
  const vehicles = useAppSelector((s) => s.vehicles.vehicles) || [];

  const [offset, setOffset] = React.useState(0);
  // "day" is the default — the existing single-day navigator.  "range" shows
  // From/To date inputs and the RangeTotalBar above the table.
  const [mode, setMode] = React.useState<"day" | "range">("day");
  // Receipts are browsed one day at a time via the date navigator (like the
  // Dump Receipts tab), defaulting to today. The day drives startDate/endDate
  // so the existing server-side date filter is unchanged.
  const [dayMoment, setDayMoment] = React.useState(() =>
    moment().startOf("day")
  );
  // Range-mode bounds (default = today).
  const [rangeStart, setRangeStart] = React.useState(() =>
    moment().startOf("day")
  );
  const [rangeEnd, setRangeEnd] = React.useState(() =>
    moment().endOf("day")
  );
  const [showDatePicker, setShowDatePicker] = React.useState(false);
  const datePickerRef = React.useRef<HTMLDivElement>(null);
  const [filters, setFilters] = React.useState<ReceiptFilters>({
    startDate: moment().startOf("day").toDate(),
    endDate: moment().endOf("day").toDate(),
  });
  const [selectedViewId, setSelectedViewId] = React.useState<string | null>(
    null
  );
  const [selectedEditId, setSelectedEditId] = React.useState<string | null>(
    null
  );
  const [selectedDeleteId, setSelectedDeleteId] = React.useState<string | null>(
    null
  );

  // Populate the User + Vehicle dropdowns
  React.useEffect(() => {
    dispatch(getDriversV2());
    dispatch(getVehicles());
  }, [dispatch]);

  // Server-side filter + pagination; re-runs whenever filters or offset change.
  const refetch = React.useCallback(() => {
    dispatch(
      getReceipts({ filters, pagination: { offset, count: PAGE_SIZE } })
    );
  }, [dispatch, filters, offset]);

  React.useEffect(() => {
    refetch();
  }, [refetch]);

  // When in range mode, (re)fetch the summary whenever filters change so the
  // total bar stays in sync with the active filter set.
  React.useEffect(() => {
    if (mode !== "range") return;
    dispatch(getReceiptsSummary({ filters }));
  }, [dispatch, filters, mode]);

  // Any server-side filter change resets to the first page.
  const applyFilters = React.useCallback((next: ReceiptFilters) => {
    setOffset(0);
    setFilters(next);
  }, []);

  // Navigate the date one day at a time (or jump via the calendar). Sets the
  // day's start/end as the server-side date range; preserves other filters.
  const goToDay = React.useCallback(
    (m: moment.Moment) => {
      const day = m.clone().startOf("day");
      setDayMoment(day);
      applyFilters({
        ...filters,
        startDate: day.toDate(),
        endDate: day.clone().endOf("day").toDate(),
      });
    },
    [applyFilters, filters]
  );

  // Switch to range mode: apply current rangeStart/rangeEnd as the date filter.
  // The summary fetch is driven by the [filters, mode] effect — no need to
  // dispatch it here (doing so would double-fetch on the initial toggle).
  const switchToRange = React.useCallback(() => {
    const next: ReceiptFilters = {
      ...filters,
      startDate: rangeStart.clone().startOf("day").toDate(),
      endDate: rangeEnd.clone().endOf("day").toDate(),
    };
    setMode("range");
    applyFilters(next);
  }, [filters, rangeStart, rangeEnd, applyFilters]);

  // Switch back to day mode: restore the single-day filter; no total bar.
  const switchToDay = React.useCallback(() => {
    setMode("day");
    goToDay(dayMoment);
  }, [goToDay, dayMoment]);

  // Called when a range date input changes. Updates the relevant moment and
  // builds the next filters; applyFilters drives the list refetch and the
  // [filters, mode] effect refetches the summary.
  const applyRangeChange = React.useCallback(
    (newStart: moment.Moment, newEnd: moment.Moment) => {
      setRangeStart(newStart);
      setRangeEnd(newEnd);
      const next: ReceiptFilters = {
        ...filters,
        startDate: newStart.clone().startOf("day").toDate(),
        endDate: newEnd.clone().endOf("day").toDate(),
      };
      applyFilters(next);
    },
    [filters, applyFilters]
  );

  // Close the calendar popover on an outside click.
  React.useEffect(() => {
    if (!showDatePicker) return;
    const onDocClick = (e: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(e.target as Node)
      ) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showDatePicker]);

  const pageIsFull = receipts.length === PAGE_SIZE;

  const columns: Column<Receipt>[] = React.useMemo(
    () => [
      {
        id: "date",
        Header: "Date",
        accessor: (row: Receipt) =>
          formatReceiptDate(row.receiptDate, row.createdAt),
      },
      {
        id: "user",
        Header: "User",
        accessor: (row: Receipt) => row.userName || "—",
      },
      {
        id: "type",
        Header: "Type",
        accessor: (row: Receipt) => receiptTypeLabel(row.receiptType),
      },
      {
        id: "truck",
        Header: "Truck",
        accessor: (row: Receipt) => row.vehicleLabel || "—",
      },
      {
        id: "cost",
        Header: "Cost",
        accessor: (row: Receipt) => formatMoney(row.cost),
      },
      {
        id: "notes",
        Header: "Notes",
        accessor: (row: Receipt) => row.note,
        Cell: (props: CellProps<Receipt, string>) =>
          props.value ? (
            <span className="receipt-note" title={props.value}>
              {props.value}
            </span>
          ) : (
            <>—</>
          ),
      },
      {
        id: "confidence",
        Header: "Confidence",
        accessor: "extractionConfidence",
        Cell: (props: CellProps<Receipt, number | null>) => {
          const band = confidenceBand(props.value);
          const label =
            props.value !== null ? `${Math.round(props.value * 100)}%` : "—";
          return (
            <span className={`confidence-pill confidence-${band}`}>{label}</span>
          );
        },
      },
      {
        id: "receipt",
        Header: "Receipt",
        accessor: "id",
        Cell: (props: CellProps<Receipt, string>) => (
          <button
            className="btn btn-sm btn-view"
            onClick={() => setSelectedViewId(props.value)}
          >
            View
          </button>
        ),
      },
      {
        id: "actions",
        Header: "Actions",
        accessor: "id",
        Cell: (props: CellProps<Receipt, string>) => (
          <div className="actions-cell">
            <button
              className="btn btn-sm btn-edit"
              onClick={() => setSelectedEditId(props.value)}
            >
              Edit
            </button>
            <button
              className="btn btn-sm btn-delete"
              onClick={() => setSelectedDeleteId(props.value)}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<Receipt>(
      {
        columns,
        data: receipts,
      },
      useSortBy
    );

  if (getReceiptsResult.loading && receipts.length === 0) {
    return (
      <div className="receipts-table-container loading">
        <div className="loading-text">Loading receipts...</div>
      </div>
    );
  }

  if (
    !getReceiptsResult.loading &&
    getReceiptsResult.error &&
    receipts.length === 0
  ) {
    return (
      <div className="receipts-table-container error">
        <div className="error-text">
          Failed to load receipts. Please try again.
        </div>
      </div>
    );
  }

  // Resolve the selected receipt object for the edit + delete modals.
  const selectedEditReceipt =
    receipts.find((r) => r.id === selectedEditId) ?? null;
  const selectedDeleteReceipt =
    receipts.find((r) => r.id === selectedDeleteId) ?? null;

  return (
    <div className="receipts-table-container">
      <div className="receipts-filter-bar">
        <label className="filter-field">
          <span>User</span>
          <select
            value={filters.userId ?? ""}
            onChange={(e) =>
              applyFilters({
                ...filters,
                userId: e.target.value || undefined,
              })
            }
          >
            <option value="">All</option>
            {drivers.map((d) => (
              <option key={d.userId} value={d.userId}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-field">
          <span>Vehicle</span>
          <select
            value={filters.vehicleId ?? ""}
            onChange={(e) =>
              applyFilters({
                ...filters,
                vehicleId: e.target.value || undefined,
              })
            }
          >
            <option value="">All</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-field">
          <span>Type</span>
          <select
            value={filters.receiptType ?? ""}
            onChange={(e) =>
              applyFilters({
                ...filters,
                receiptType: (e.target.value as ReceiptType) || undefined,
              })
            }
          >
            <option value="">All</option>
            <option value="gas">Gas</option>
            <option value="misc">Misc</option>
          </select>
        </label>
        <div className="filter-field">
          <div className="receipts-date-nav">
            {/* Day | Range segmented toggle */}
            <div className="date-mode-toggle">
              <button
                type="button"
                className={`date-mode-btn${mode === "day" ? " date-mode-btn--active" : ""}`}
                onClick={switchToDay}
              >
                Day
              </button>
              <button
                type="button"
                className={`date-mode-btn${mode === "range" ? " date-mode-btn--active" : ""}`}
                onClick={switchToRange}
              >
                Range
              </button>
            </div>
            {mode === "day" ? (
              <>
                <button
                  type="button"
                  className="date-nav-arrow"
                  aria-label="Previous day"
                  onClick={() => goToDay(dayMoment.clone().add(-1, "day"))}
                >
                  <BsChevronLeft />
                </button>
                <div className="date-nav-anchor" ref={datePickerRef}>
                  <button
                    type="button"
                    className="date-nav-text"
                    onClick={() => setShowDatePicker((p) => !p)}
                  >
                    {dayMoment.format("ddd, Do MMM YYYY")}
                  </button>
                  {showDatePicker && (
                    <div className="date-nav-popover">
                      <DateCalendar
                        value={dayMoment}
                        onChange={(m) => {
                          if (m) {
                            goToDay(m);
                            setShowDatePicker(false);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="date-nav-arrow"
                  aria-label="Next day"
                  onClick={() => goToDay(dayMoment.clone().add(1, "day"))}
                >
                  <BsChevronRight />
                </button>
              </>
            ) : (
              <div className="date-range-inputs">
                <label className="date-range-label" htmlFor="range-from">
                  From
                  <input
                    id="range-from"
                    type="date"
                    value={rangeStart.format("YYYY-MM-DD")}
                    onChange={(e) => {
                      const m = moment(e.target.value, "YYYY-MM-DD", true);
                      if (m.isValid()) applyRangeChange(m, rangeEnd);
                    }}
                  />
                </label>
                <label className="date-range-label" htmlFor="range-to">
                  To
                  <input
                    id="range-to"
                    type="date"
                    value={rangeEnd.format("YYYY-MM-DD")}
                    onChange={(e) => {
                      const m = moment(e.target.value, "YYYY-MM-DD", true);
                      if (m.isValid()) applyRangeChange(rangeStart, m);
                    }}
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
      {mode === "range" && (
        <RangeTotalBar summary={summary} loading={summaryResult.loading} />
      )}
      <div className="table-container">
        <div className="table-scroll-wrapper">
          <table {...getTableProps()} className="receipts-table">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>
                      {column.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.length === 0 ? (
                <tr>
                  <td className="empty-row" colSpan={columns.length}>
                    No receipts found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="receipts-pagination">
        <button
          className="btn btn-sm"
          disabled={offset === 0}
          onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
        >
          Prev
        </button>
        <span className="page-indicator">Page {offset / PAGE_SIZE + 1}</span>
        <button
          className="btn btn-sm"
          disabled={!pageIsFull}
          onClick={() => setOffset((o) => o + PAGE_SIZE)}
        >
          Next
        </button>
      </div>
      <ReceiptModal
        receiptId={selectedViewId}
        onClose={() => setSelectedViewId(null)}
      />
      <EditReceiptModal
        receipt={selectedEditReceipt}
        onClose={() => setSelectedEditId(null)}
        onSaved={() => {
          setSelectedEditId(null);
          refetch();
        }}
      />
      <DeleteReceiptConfirmationModal
        receipt={selectedDeleteReceipt}
        onClose={() => setSelectedDeleteId(null)}
        onDeleted={() => {
          setSelectedDeleteId(null);
          refetch();
        }}
      />
    </div>
  );
}
